'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { segmentClient } from '@/lib/analytics/segment';
import type { ResumableGameInfo } from '@/app/hooks/useResumableGame';

interface ResumeGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResume: () => void;
  onStartNew: () => void;
  resumableGame: ResumableGameInfo;
}

/**
 * Modal that appears when a player has a resumable game.
 * Shows the character standing in the game world background.
 */
export function ResumeGameModal({
  isOpen,
  onClose,
  onResume,
  onStartNew,
  resumableGame,
}: ResumeGameModalProps) {
  const [isResuming, setIsResuming] = useState(false);
  const [isStartingNew, setIsStartingNew] = useState(false);

  const isLoading = isResuming || isStartingNew;

  // Reset loading state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsResuming(false);
      setIsStartingNew(false);
    }
  }, [isOpen]);

  // Track modal display
  useEffect(() => {
    if (isOpen) {
      segmentClient.track('Screen Displayed', {
        eventCategory: 'game resume',
        displayChoices: ['Continue Adventure', 'Start New Game', 'Close'],
        text: `Resume game modal for stage ${resumableGame.current_stage}`,
        source: 'client',
        status: 'handled',
      });
    }
  }, [isOpen, resumableGame.current_stage]);

  if (!isOpen) return null;

  const handleResume = () => {
    setIsResuming(true);
    segmentClient.track('Button Pressed', {
      eventCategory: 'game resume',
      choiceValue: 'Continue Adventure',
      displayChoices: ['Continue Adventure', 'Start New Game', 'Close'],
      source: 'client',
      message: `User chose to resume game at stage ${resumableGame.current_stage}`,
      status: 'handled',
    });
    onResume();
  };

  const handleStartNew = () => {
    setIsStartingNew(true);
    segmentClient.track('Button Pressed', {
      eventCategory: 'game resume',
      choiceValue: 'Start New Game',
      displayChoices: ['Continue Adventure', 'Start New Game', 'Close'],
      source: 'client',
      message: 'User chose to start new game instead of resuming',
      status: 'handled',
    });
    onStartNew();
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while loading
    segmentClient.track('Button Pressed', {
      eventCategory: 'game resume',
      choiceValue: 'Close',
      displayChoices: ['Continue Adventure', 'Start New Game', 'Close'],
      source: 'client',
      message: 'User closed resume modal',
      status: 'handled',
    });
    onClose();
  };

  // Format last saved time
  const formatLastSaved = (isoString: string | null): string => {
    if (!isoString) return 'Recently';

    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const character = resumableGame.character;
  const backgroundImageUrl = resumableGame.background_image_url;
  const backgroundVideoUrl = resumableGame.background_video_url;

  return (
    <div className="fixed inset-0 z-[300] bg-black flex flex-col">
      {/* Mobile layout */}
      <div className="md:hidden flex flex-col flex-1">
        {/* Full-screen background */}
        <div className="absolute inset-0 overflow-hidden">
          {backgroundImageUrl ? (
            <div
              className="w-full h-full bg-cover bg-bottom"
              style={{
                backgroundImage: `url(${backgroundImageUrl})`,
                transform: 'translateY(-100px)',
              }}
            />
          ) : backgroundVideoUrl ? (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src={backgroundVideoUrl}
              autoPlay
              loop
              muted
              playsInline
              style={{ transform: 'translateY(-150px)' }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-b from-[#2a1a3a] via-[#1a1a2e] to-[#0a0a14]" />
          )}
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/40" />
          {/* Vignette */}
          <div className="absolute inset-0 shadow-[inset_0_0_150px_60px_rgba(0,0,0,0.8)]" />
          {/* Ground gradient */}
          <div className="absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t from-black via-black/90 to-transparent" />
        </div>

        {/* Close button - prominent */}
        <button
          onClick={handleClose}
          disabled={isLoading}
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/80 backdrop-blur-sm
                     flex items-center justify-center hover:bg-black transition-all duration-200 z-20
                     disabled:opacity-50 disabled:cursor-not-allowed border-2 border-white/40
                     shadow-lg shadow-black/50 hover:scale-110 active:scale-95"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </button>

        {/* Top section - Welcome header */}
        <div className="relative z-10 pt-14 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="text-amber-400 text-lg">✦</span>
            <h1 className="text-3xl font-bold text-white tracking-wide font-macondoSwashCaps drop-shadow-lg">
              Welcome Back
            </h1>
            <span className="text-amber-400 text-lg">✦</span>
          </div>
          <p className="text-base text-amber-200/80">You have an adventure in progress</p>
        </div>

        {/* Bottom section - Character standing on ground with info and buttons */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-end pb-8 px-6">
          {/* Character standing on the ground */}
          {character?.profile_picture_url && (
            <div className="relative mb-3">
              {/* Character glow on ground */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-36 h-16 bg-amber-500/30 blur-[40px] rounded-full" />
              {/* Ground shadow - ellipse under feet */}
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-24 h-5 bg-black/70 blur-md rounded-[100%]" />
              {/* Character image */}
              <div className="relative w-40 h-52">
                <Image
                  src={character.profile_picture_url}
                  alt={character.name || 'Character'}
                  fill
                  className="object-contain object-bottom drop-shadow-[0_0_30px_rgba(255,191,0,0.4)]"
                  unoptimized
                />
              </div>
            </div>
          )}

          {/* Character name and progress */}
          <div className="text-center mb-6">
            {character?.name && (
              <h2 className="text-3xl font-macondoSwashCaps text-white drop-shadow-lg mb-1">
                {character.name}
              </h2>
            )}
            <p className="text-base text-amber-400 font-medium">
              {resumableGame.current_location_name || `Stage ${resumableGame.current_stage}`}
              <span className="mx-2 text-amber-600">•</span>
              {formatLastSaved(resumableGame.last_saved_at)}
            </p>
          </div>

          {/* Buttons - matching landing page style */}
          <div className="flex flex-col gap-3 items-center w-full">
            <button
              onClick={handleResume}
              disabled={isLoading}
              className="rounded-[86px] bg-gradient-to-b from-[rgba(255,191,96,0.25)] to-[rgba(255,152,0,0.61)]
                         backdrop-blur-md w-full max-w-xs h-[56px] text-white text-[20px] font-bold
                         transition-all duration-200 hover:scale-105
                         hover:from-[rgba(255,191,96,0.35)] hover:to-[rgba(255,152,0,0.71)]
                         active:scale-95 drop-shadow-lg
                         disabled:hover:scale-100 disabled:opacity-70
                         flex items-center justify-center gap-2"
            >
              {isResuming ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Continuing...</span>
                </>
              ) : (
                'Continue Adventure'
              )}
            </button>

            <button
              onClick={handleStartNew}
              disabled={isLoading}
              className="rounded-[86px] bg-[rgba(255,191,96,0.10)] border-2 border-white/5
                         backdrop-blur-sm w-full max-w-xs h-[56px] text-white text-[20px] font-bold
                         transition-all duration-200 hover:bg-[rgba(255,191,96,0.20)]
                         active:scale-95
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
            >
              {isStartingNew ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Starting...</span>
                </>
              ) : (
                'Start New Game'
              )}
            </button>
          </div>

          <p className="text-xs text-white/50 text-center mt-3">
            Starting a new game will replace your saved progress
          </p>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:block fixed inset-0 h-screen max-h-screen overflow-hidden [@media(max-height:800px)]:overflow-y-auto">
        <div className="flex flex-col min-h-[800px] h-full relative">
          {/* Full-screen background - fills the same min-h container (avoids black gaps on short screens) */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {backgroundImageUrl ? (
              <div
                className="w-full h-full bg-cover bg-bottom"
                style={{
                  backgroundImage: `url(${backgroundImageUrl})`,
                  transform: 'translateY(-150px)',
                }}
              />
            ) : backgroundVideoUrl ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                src={backgroundVideoUrl}
                autoPlay
                loop
                muted
                playsInline
                style={{ transform: 'translateY(-150px)' }}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-b from-[#2a1a3a] via-[#1a1a2e] to-[#0a0a14]" />
            )}
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-black/40" />
            {/* Vignette */}
            <div className="absolute inset-0 shadow-[inset_0_0_200px_80px_rgba(0,0,0,0.8)]" />
            {/* Ground gradient - creates the illusion of ground at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-black via-black/90 to-transparent" />
          </div>

          {/* Close button - prominent (fixed so it stays visible while scrolling) */}
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="fixed top-6 right-6 w-14 h-14 rounded-full bg-black/80 backdrop-blur-sm
                       flex items-center justify-center hover:bg-black transition-all duration-200 z-20
                       disabled:opacity-50 disabled:cursor-not-allowed border-2 border-white/40
                       shadow-lg shadow-black/50 hover:scale-110 active:scale-95"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </button>

          {/* Top section - Welcome header */}
          <div className="relative z-10 pt-10 text-center">
            <div className="flex items-center justify-center gap-4 mb-3">
              <span className="text-amber-400 text-2xl">✦</span>
              <h1 className="text-5xl font-bold text-white tracking-wide font-macondoSwashCaps drop-shadow-lg">
                Welcome Back
              </h1>
              <span className="text-amber-400 text-2xl">✦</span>
            </div>
            <p className="text-xl text-amber-200/80">You have an adventure in progress</p>
          </div>

          {/* Main content area - sizing like NewLaptopGame (character scales with viewport height) */}
          <div className="relative z-10 flex-1 flex flex-col min-h-0">
            {/* Character area - takes available space, character at bottom */}
            <div className="flex-1 relative flex items-end justify-center">
              {character?.profile_picture_url && (
                <div className="relative mb-4">
                  {/* Character glow on ground - scales with character */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[60%] aspect-[3/1] bg-amber-500/30 blur-[60px] rounded-full" />
                  {/* Ground shadow - ellipse under feet */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[40%] aspect-[4/1] bg-black/70 blur-lg rounded-[100%]" />
                  {/* Character image - responsive sizing (avoid vertical overflow on shorter screens) */}
                  <div className="relative h-[clamp(320px,42vh,520px)] aspect-[3/4]">
                    <Image
                      src={character.profile_picture_url}
                      alt={character.name || 'Character'}
                      fill
                      className="object-contain object-bottom drop-shadow-[0_0_50px_rgba(255,191,0,0.5)]"
                      unoptimized
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Info and buttons section - fixed at bottom */}
            <div className="pb-8">
              {/* Character name and progress */}
              <div className="text-center mb-6">
                {character?.name && (
                  <h2 className="text-4xl font-macondoSwashCaps text-white drop-shadow-lg mb-2">
                    {character.name}
                  </h2>
                )}
                <p className="text-lg text-amber-400 font-medium">
                  {resumableGame.current_location_name || `Stage ${resumableGame.current_stage}`}
                  <span className="mx-3 text-amber-600">•</span>
                  {formatLastSaved(resumableGame.last_saved_at)}
                </p>
              </div>

              {/* Buttons - matching landing page style */}
              <div className="flex flex-col gap-3 items-center">
                <button
                  onClick={handleResume}
                  disabled={isLoading}
                  className="rounded-[86px] bg-gradient-to-b from-[rgba(255,191,96,0.25)] to-[rgba(255,152,0,0.61)]
                             backdrop-blur-md w-80 h-[60px] text-white text-[22px] font-bold
                             transition-all duration-200 hover:scale-105
                             hover:from-[rgba(255,191,96,0.35)] hover:to-[rgba(255,152,0,0.71)]
                             active:scale-95 drop-shadow-lg
                             disabled:hover:scale-100 disabled:opacity-70
                             flex items-center justify-center gap-3"
                >
                  {isResuming ? (
                    <>
                      <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Continuing...</span>
                    </>
                  ) : (
                    'Continue Adventure'
                  )}
                </button>

                <button
                  onClick={handleStartNew}
                  disabled={isLoading}
                  className="rounded-[86px] bg-[rgba(255,191,96,0.10)] border-2 border-white/5
                             backdrop-blur-sm w-80 h-[60px] text-white text-[22px] font-bold
                             transition-all duration-200 hover:bg-[rgba(255,191,96,0.20)]
                             active:scale-95
                             disabled:opacity-50 disabled:cursor-not-allowed
                             flex items-center justify-center gap-2"
                >
                  {isStartingNew ? (
                    <>
                      <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Starting...</span>
                    </>
                  ) : (
                    'Start New Game'
                  )}
                </button>
              </div>

              <p className="text-sm text-white/50 text-center mt-4">
                Starting a new game will replace your saved progress
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
